import React, { useContext, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { NavLink, useHistory } from 'react-router-dom';
import { useAuthorization } from '../../components/Authorization/useAuthorization';
import Card from '../../components/Cards/Card';
import { Add, Edit, Remove } from '../../components/Icons/Icons';
import AdminContext from '../Settings/AdminContext/AdminContext';
import { useMedicalCenters } from './Hooks';
import { Spinner } from '../../components/Utils/Loaders';
import { Tooltip } from '../../components/Utils/Tooltips';

export const MedicalCentersContainer = () => {
    const { t } = useTranslation();
    let history = useHistory();
    const [, , isAuthorized] = useAuthorization();
    const { selectedTenant } = useContext(AdminContext);

    const isSuperAdmin = isAuthorized(['Tenant.View']);
    const tenantId = isSuperAdmin ? selectedTenant?.tenantId ?? null : null;

    const [medicalCenters, isLoading, getAll, , , , remove] = useMedicalCenters(tenantId);

    useEffect(() => {
        if (isSuperAdmin && !selectedTenant) return;
        let isSubscribed = true;
        getAll();
        return () => { isSubscribed = false; };
    }, [selectedTenant]);

    const handleRemove = async (id) => {
        if (!window.confirm(t('medicalCenter.confirmDelete', 'Are you sure?'))) return;
        try {
            await remove(id);
            getAll();
        } catch (error) {
            // HandleApiError es invocado dentro de remove si falla la API
        }
    };

    const handleEdit = (id) => {
        const path = tenantId
            ? `/admin/settings/medicalcenters/${id}?tenantId=${encodeURIComponent(tenantId)}`
            : `/admin/settings/medicalcenters/${id}`;
        history.push(path);
    }

    if (isSuperAdmin && !selectedTenant) {
        return (
            <Card
                content={
                    <p className="text-muted" style={{ margin: '16px 0' }}>
                        {t('medicalCenter.selectTenantFirst', 'Seleccioná un cliente para ver sus centros médicos.')}
                    </p>
                }
            />
        );
    }

    if (isLoading) return <Spinner />;

    return (
        <Card
            tabs
            header={
                <div className="row">
                    <div className="col-md-2">
                        {(isSuperAdmin || isAuthorized(["MedicalCenter.Create"])) && (
                            <NavLink
                                to={tenantId
                                    ? `/admin/settings/medicalcenters/create?tenantId=${encodeURIComponent(tenantId)}`
                                    : '/admin/settings/medicalcenters/create'}
                                className="btn btn-primary"
                                activeClassName="active"
                            >
                                <Add /> {t('medicalCenter.add', 'Agregar Centro')}
                            </NavLink>
                        )}
                    </div>
                </div>
            }
            content={
                <table className="table table-hover">
                    <thead>
                        <tr>
                            <th>{t('medicalCenter.name', 'Nombre')}</th>
                            <th>{t('medicalCenter.active', 'Activo')}</th>
                            <th>{t('medicalCenter.created', 'Creado')}</th>
                            <th></th>
                        </tr>
                    </thead>
                    <tbody>
                        {medicalCenters.map(c => (
                            <tr key={c.medicalCenterId}>
                                <td>{c.name}</td>
                                <td>{c.isActive ? t('common.yes', 'Sí') : t('common.no', 'No')}</td>
                                <td>{c.created ? new Date(c.created).toLocaleDateString() : '-'}</td>
                                <td className="text-right">
                                    {(isSuperAdmin || isAuthorized(["MedicalCenter.Edit"])) && (
                                        <button className="btn-table-actions" onClick={() => handleEdit(c.medicalCenterId)}>
                                            <Tooltip tooltip={t("buttons.edit")} id={"tooltipedit-" + c.medicalCenterId}>
                                                <span aria-hidden="true"><Edit /></span>
                                            </Tooltip>
                                        </button>
                                    )}
                                    {' '}
                                    {(isSuperAdmin || isAuthorized(["MedicalCenter.Delete"])) && (
                                        <button className="btn-table-actions" onClick={() => handleRemove(c.medicalCenterId)}>
                                            <Tooltip tooltip={t("commons.remove")} id={"tooltipremove-" + c.medicalCenterId}>
                                                <span aria-hidden="true"><Remove /></span>
                                            </Tooltip>
                                        </button>
                                    )}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            }
        />
    );
};
